<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { until } from '@vueuse/core'
import { CaretDownFilled, CaretUpFilled } from '@ant-design/icons-vue'
import { flatten } from 'lodash-es'
import { bin, thresholdSturges } from 'd3-array'

import ChartDescription from '@/components/Charts/Common/ChartDescription.vue'
import PageHeading from '@/components/ContentSections/PageHeading.vue'
import LabelWithValueWithUnderline from '@/components/ContentSections/LabelWithValueWithUnderline.vue'
import EmissionsSummary from '@/components/Cards/EmissionSummary.vue'
import ByCommodity from '@/components/Charts/Analytics/EmissionsByCommodity/ByCommodity.vue'
import HeadlineStat from '@/components/ContentSections/HeadlineStat.vue'
import AnalyticsMap from '@/components/Geo/AnalyticsMap.vue'
import IntensitySelector from '@/components/IntensitySelector.vue'
import AnalyticsFilters from '@/components/ContentSections/AnalyticsFilters.vue'
import AnalyticsCounts from '@/components/ContentSections/AnalyticsCounts.vue'
import AsHistogram from '@/components/Charts/Analytics/Intensity/AsHistogram.vue'

import { useCohorts } from '@/composables/cohorts'
import { useAnalytics } from '@/composables/analytics'

import { formatNumberToLocaleStringWithDecimalPlaces } from '@/utilities/numbers'
import { reportYearsAsOptions } from '@/utilities/reports'
import { absoluteDifference } from '@/utilities/maths'

const { cohortsAsOptions } = useCohorts()

const selectedYear = ref(reportYearsAsOptions[0].value)
const selectedCohortIds = ref<string[]>([])
const selectedRegionIds = ref<number[]>([])
const useIntensityThresholds = ref(true)

const {
  relevantIntensityMetrics,
  relevantMajorCommodities,
  sumTotalNetSequestration,
  reportsMatchingAllFilters,
  intensityAverages,
  commodityTotals,
  propertiesMatchingFilter,
} = useAnalytics({
  filters: {
    useIntensityThresholds,
    year: selectedYear,
    stateIds: selectedRegionIds,
    cohortIds: selectedCohortIds,
  },
})

const selectedIntensityMetricId = ref('')

onMounted(async () => {
  await until(
    () => relevantIntensityMetrics.value && relevantIntensityMetrics.value?.length > 0,
  ).toBe(true)
  if (relevantIntensityMetrics.value && relevantIntensityMetrics.value?.length > 0) {
    const metric =
      relevantIntensityMetrics.value.findIndex((m) => m.value === 'beef') > -1
        ? 'beef'
        : relevantIntensityMetrics.value?.[0].value
    selectedIntensityMetricId.value = metric
  }
})

const selectedMetric = computed(() => {
  return relevantIntensityMetrics.value.find((ic) => ic.value === selectedIntensityMetricId.value)!
})

const selectedIntensityStats = computed(() => {
  const intensityResult = intensityAverages.value[selectedIntensityMetricId.value]
  if (!intensityResult || intensityResult.avg === null) {
    return {
      count: 0,
    }
  }

  const avgToCompareAgainst = selectedMetric.value?.intensityReference?.nationalAverage

  const differenceToAvg = avgToCompareAgainst
    ? formatNumberToLocaleStringWithDecimalPlaces(
        Math.abs(absoluteDifference(intensityResult.avg, avgToCompareAgainst)),
        selectedMetric.value?.decimalPrecision,
      )
    : ''
  const lessThanAverage = avgToCompareAgainst ? intensityResult.avg < avgToCompareAgainst : false

  return {
    average: formatNumberToLocaleStringWithDecimalPlaces(
      intensityResult.avg,
      selectedMetric.value?.decimalPrecision,
    ),
    count: intensityResult.countOfRecords,
    differenceToAvg,
    lessThanAverage,
  }
})
const commodityTotalValues = computed(() => {
  return Object.values(commodityTotals.value)
})

const emissionsHistogram = computed<{
  data: number[]
  labels: string[]
  range: [number, number]
}>(() => {
  const metricId = selectedIntensityMetricId.value
  const intensityResult = intensityAverages.value[metricId]

  const values: number[] = intensityResult.records.map((r) => r.value)
  const bins = bin().thresholds(thresholdSturges)(values)
  const counts = bins.map((b) => b.length)
  const labels = bins.map((b) => {
    return `${typeof b.x0 === 'number' ? formatNumberToLocaleStringWithDecimalPlaces(b.x0, 1) : ''} - ${typeof b.x1 === 'number' ? formatNumberToLocaleStringWithDecimalPlaces(b.x1, 1) : ''}`
  })
  const rangeBins = flatten(bins.map((b) => [b.x0, b.x1])).filter((v) => v) as number[]

  return {
    data: counts,
    labels: labels,
    range: [Math.min(...rangeBins), Math.max(...rangeBins)],
  }
})

const useEmissionsIntensityDistribution = ref(false)
</script>

<template>
  <PageHeading title="Annual Overview">
    <template #right>
      <AnalyticsCounts
        :reports-matching-all-filters-length="reportsMatchingAllFilters.reports.length"
        :failed-intensity-filter="reportsMatchingAllFilters.exclusionStats.failedIntensityFilter"
      />
    </template>
  </PageHeading>

  <AnalyticsFilters
    :selected-year="selectedYear"
    :set-selected-year="(year) => (selectedYear = year)"
    :selected-cohort-ids="selectedCohortIds"
    :cohorts-as-options="cohortsAsOptions"
    :set-selected-cohort-ids="(cohortIds) => (selectedCohortIds = cohortIds)"
    is-multi-state-org
    :selected-state-ids="selectedRegionIds"
    :set-selected-state-ids="(stateIds) => (selectedRegionIds = stateIds)"
    use-intensity-thresholds-section
    :use-intensity-thresholds="useIntensityThresholds"
    :set-use-intensity-thresholds="(checked) => (useIntensityThresholds = checked)"
  />

  <EmissionsSummary
    :total="sumTotalNetSequestration.emissions"
    :sequestration="sumTotalNetSequestration.sequestration"
    :net="sumTotalNetSequestration.net"
    :hide-sequestration-tooltip="true"
  />

  <a-flex class="gap-col-3xl pb-32">
    <a-flex vertical flex="0 0 420px">
      <div class="padding-lg border-rd-lg bg-rumi-grey-100">
        <h3>Average Emissions Intensity</h3>
        <ChartDescription
          description="Emissions intensity is an important measure to show how
            'emissions-efficient' businesses are at producing various
            commodities."
        />
        <a-flex class="mt-5 mb-5">
          <IntensitySelector
            :selected-metric-id="selectedIntensityMetricId"
            :on-select="(key) => (selectedIntensityMetricId = key)"
          />
        </a-flex>
        <a-flex
          v-if="selectedIntensityStats.count === 0"
          class="border-rd-md bg-rumi-grey-400 mt-4 p-4"
          justify="center"
        >
          <a-flex vertical justify="center" class="min-h-[110px]">
            <p class="text-rumi-grey-700 text-center">
              No data available for <br />
              the selected year and filters
            </p>
          </a-flex>
        </a-flex>
        <a-flex v-else class="border-rd-md bg-rumi-grey-300 mt-4 w-full p-4">
          <a-flex vertical class="w-full">
            <a-flex justify="flex-end" class="mb-2">
              <p class="text-rumi-grey-650 mr-2 text-sm">Show Histogram</p>
              <a-switch v-model:checked="useEmissionsIntensityDistribution" />
            </a-flex>
            <Transition name="fade" mode="out-in">
              <a-flex v-if="!useEmissionsIntensityDistribution">
                <a-flex vertical class="w-full">
                  <HeadlineStat
                    :unit="selectedMetric.unitLabel"
                    :value="selectedIntensityStats.average ?? ''"
                  />

                  <a-flex
                    v-if="selectedMetric.intensityReference"
                    class="border-rd-sm mt-3 px-[10px] py-[2px]"
                    :class="{
                      'bg-rumi-tan-400': !selectedIntensityStats.lessThanAverage,
                      'bg-rumi-silver-green-300': selectedIntensityStats.lessThanAverage,
                    }"
                  >
                    <p
                      :class="{
                        'text-rumi-jungle-green-700': selectedIntensityStats.lessThanAverage,
                      }"
                    >
                      <CaretDownFilled v-if="selectedIntensityStats.lessThanAverage" />
                      <CaretUpFilled v-else />
                      {{ selectedIntensityStats.differenceToAvg }}kg's
                      {{ selectedIntensityStats.lessThanAverage ? 'below' : 'above' }} national avg
                      of {{ selectedMetric.intensityReference?.nationalAverage }}kg
                    </p>
                  </a-flex>
                  <LabelWithValueWithUnderline
                    label="Number of reports"
                    :value="selectedIntensityStats.count.toString()"
                    class="mt-2"
                  />
                </a-flex>
              </a-flex>
              <a-flex v-else>
                <AsHistogram
                  :data="emissionsHistogram.data"
                  :unit-label="selectedMetric.unitLabel"
                  :suggested-max="
                    (selectedMetric.intensityReference?.nationalAverage
                      ? selectedMetric.intensityReference?.nationalAverage
                      : 0) + 1
                  "
                  :national-avg="selectedMetric.intensityReference?.nationalAverage"
                  :national-avg-label="selectedMetric.intensityReference?.author"
                  :labels="emissionsHistogram.labels"
                  :range="emissionsHistogram.range"
                />
              </a-flex>
            </Transition>
          </a-flex>
        </a-flex>
      </div>

      <div class="padding-lg border-rd-lg bg-rumi-grey-100 mt-3xl">
        <h3>Emissions By Commodity</h3>
        <a-flex
          v-if="!commodityTotalValues.some((v) => v > 0)"
          class="border-rd-md bg-rumi-grey-400 mt-4 p-4"
          justify="center"
        >
          <a-flex vertical justify="center" class="min-h-[110px]">
            <p class="text-rumi-grey-700 text-center">
              No data available for <br />
              the selected year and filters
            </p>
          </a-flex>
        </a-flex>
        <ByCommodity
          v-else
          :labels="relevantMajorCommodities.map((c) => c.label)"
          :data="commodityTotalValues"
        />
      </div>
    </a-flex>
    <a-flex vertical flex="auto">
      <div class="padding-lg border-rd-lg bg-rumi-grey-100">
        <h3>Distribution & Variance</h3>
        <a-flex class="w-full">
          <ChartDescription
            description="Visualise the spatial distribution & variance of emissions & intensity for your Linked Businesses."
          />
        </a-flex>

        <AnalyticsMap
          :reports="reportsMatchingAllFilters?.reports"
          :properties="propertiesMatchingFilter"
          :relevant-intensity-metrics="relevantIntensityMetrics"
        />
      </div>
    </a-flex>
  </a-flex>
</template>
